import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Chat,
  CheckCircle,
  AccessTime,
  Star,
  People,
  Schedule
} from '@mui/icons-material';
import { reportsAPI } from '../../services/api';
import { ExportButton } from './ExportButton';

interface OverviewDashboardProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  format?: 'number' | 'percentage' | 'time' | 'rating';
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  change, 
  icon, 
  color, 
  format = 'number' 
}) => {
  const theme = useTheme();
  
  const formatValue = (val: string | number) => {
    if (format === 'percentage') return `${val}%`;
    if (format === 'time') return `${val} min`;
    if (format === 'rating') return `${val}/5.0`;
    if (format === 'number' && typeof val === 'number') {
      return val.toLocaleString();
    }
    return val;
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return theme.palette.success.main;
    if (change < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ mb: 1, color: `${color}.main` }}>
              {formatValue(value)}
            </Typography>
            {change !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {change > 0 ? (
                  <TrendingUp sx={{ color: getChangeColor(change), fontSize: 16, mr: 0.5 }} />
                ) : change < 0 ? (
                  <TrendingDown sx={{ color: getChangeColor(change), fontSize: 16, mr: 0.5 }} />
                ) : null}
                <Typography 
                  variant="body2" 
                  sx={{ color: getChangeColor(change) }}
                >
                  {change > 0 ? '+' : ''}{change.toFixed(1)}%
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ 
            backgroundColor: `${color}.light`, 
            borderRadius: 2, 
            p: 1.5,
            color: `${color}.main`
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const OverviewDashboard: React.FC<OverviewDashboardProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [conversationReport, setConversationReport] = useState<any>(null);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch dashboard metrics and conversation report
      const [dashboard, conversations] = await Promise.all([
        reportsAPI.getDashboardMetrics(),
        reportsAPI.getConversationReport({
          start_date: dateRange.startDate.toISOString().split('T')[0],
          end_date: dateRange.endDate.toISOString().split('T')[0]
        })
      ]);
      
      setDashboardData(dashboard);
      setConversationReport(conversations);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!dashboardData || !conversationReport) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No data available for the selected period.
      </Alert>
    );
  }

  const metrics = [
    {
      title: 'Total Conversations',
      value: dashboardData.total_conversations || conversationReport.total_conversations,
      change: dashboardData.changes?.conversations,
      icon: <Chat />,
      color: 'primary' as const
    },
    {
      title: 'Resolution Rate',
      value: dashboardData.resolution_rate || Math.round((conversationReport.resolved_conversations / conversationReport.total_conversations) * 100),
      change: dashboardData.changes?.resolution_rate,
      icon: <CheckCircle />,
      color: 'success' as const,
      format: 'percentage' as const
    },
    {
      title: 'Avg First Response Time',
      value: dashboardData.avg_first_response_time || Math.round(conversationReport.avg_first_response_time),
      change: dashboardData.changes?.first_response_time,
      icon: <AccessTime />,
      color: 'info' as const,
      format: 'time' as const
    },
    {
      title: 'Customer Satisfaction',
      value: dashboardData.avg_csat_30_days,
      change: dashboardData.changes?.csat,
      icon: <Star />,
      color: 'warning' as const,
      format: 'rating' as const
    },
    {
      title: 'Open Conversations',
      value: dashboardData.open_conversations,
      icon: <Schedule />,
      color: 'secondary' as const
    },
    {
      title: 'Pending Conversations',
      value: dashboardData.pending_conversations,
      icon: <People />,
      color: 'error' as const
    }
  ];

  // Prepare data for export
  const exportData = [
    ...metrics.map(m => ({
      metric: m.title,
      value: m.value,
      change_percentage: m.change,
      color: m.color
    })),
    ...Object.entries(conversationReport.conversations_by_channel || {}).map(([channel, count]) => ({
      channel_name: channel,
      conversation_count: count
    }))
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Overview Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Key performance metrics for your customer support operations
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename="overview_dashboard"
          metadata={{ dateRange }}
        />
      </Box>

      <Grid container spacing={3}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      {/* Additional Summary Cards */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Conversations by Channel
              </Typography>
              <Box>
                {Object.entries(conversationReport.conversations_by_channel).map(([channel, count]) => (
                  <Box key={channel} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {channel}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {count as number}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Distribution
              </Typography>
              <Box>
                {Object.entries(conversationReport.conversations_by_status).map(([status, count]) => (
                  <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {status}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {count as number}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};